
import { TypedVue } from "@/store/types";
import { Component, Inject, Prop } from "vue-property-decorator";
import VueI18n from "vue-i18n";
import { EvaluationReportData } from "@/store/admin/adminTypes";
import { buildCandidateSummaries, CandidateSummary } from "./shared";
import ParticipantTag from "./ParticipantTag.vue";
import CandidatePicker from "@/components/project-reports/CandidatePicker.vue";

interface QuestionCard {
  trait: string;
  name: string;
  flag: "MustHave" | "Neutral";
  tag: string;
  tagClass: string;
  question: string;
  listenFor: string;
}

interface FocusPill {
  trait: string;
  name: string;
  kind: "must" | "neutral";
  tag: string;
}

@Component({ components: { ParticipantTag, CandidatePicker } })
export default class SummaryNextStepsTab extends TypedVue {
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true })
  readonly reportData!: EvaluationReportData;

  @Prop({ required: true })
  readonly sortedAssessments!: EvaluationReportData["assessments"];

  @Prop({ default: false })
  readonly printing!: boolean;

  public selectedCandidateId: string | null = null;

  // Candidates only — discussion prompts and interview questions are per candidate.
  get summaries(): CandidateSummary[] {
    return buildCandidateSummaries(this.sortedAssessments, this.reportData).filter((s) => s.isCandidate);
  }

  get selectedSummary(): CandidateSummary | null {
    return this.summaries.find((s) => s.id === this.selectedCandidateId) ?? null;
  }

  get visibleSummaries(): CandidateSummary[] {
    if (this.printing) return this.summaries;
    return this.selectedSummary ? [this.selectedSummary] : [];
  }

  focusPillsFor(summary: CandidateSummary): FocusPill[] {
    return summary.watchOuts.map(({ trait, level }) => ({
      trait,
      name: this.$t(`Comparison::TraitChart::XAxisLabel::${trait}`).toString(),
      kind: level === 2 ? "must" : "neutral",
      tag: this.$t(level === 2 ? "NextSteps::Tag::MustHaveGap" : "NextSteps::Tag::HighPriorityNeutral").toString(),
    }));
  }

  // Watch-out-driven questions; when a candidate has no watch-outs, fall back to
  // one question per must-have competency instead.
  questionsFor(summary: CandidateSummary): QuestionCard[] {
    const build = (trait: string, flag: "MustHave" | "Neutral", tag: string): QuestionCard => ({
      trait,
      name: this.$t(`Comparison::TraitChart::XAxisLabel::${trait}`).toString(),
      flag,
      tag,
      tagClass: flag === "MustHave" ? "tag-mh" : "tag-nt",
      question: this.$t(`InterviewQuestion::${trait}::${flag}`).toString(),
      listenFor: this.$t(`InterviewQuestion::Outcome::${trait}::${flag}`).toString(),
    });

    if (summary.watchOuts.length) {
      return summary.watchOuts.map(({ trait, level }) =>
        level === 2 ? build(trait, "MustHave", this.$t("NextSteps::Tag::MustHaveGap").toString()) : build(trait, "Neutral", this.$t("NextSteps::Tag::HighPriorityNeutral").toString()),
      );
    }

    const mustHaveTraits = Object.keys(this.reportData.traitPriorities ?? {}).filter((t) => this.reportData.traitPriorities[t] === 2);
    return mustHaveTraits.map((trait) => build(trait, "MustHave", this.$t("NextSteps::Tag::MustHave").toString()));
  }

  mounted() {
    this.selectedCandidateId = this.summaries[0]?.id ?? null;
  }
}
